'use client'

import { Star, Check, Sparkles, TrendingUp } from 'lucide-react'
import { motion } from 'framer-motion'
import { useState, useRef } from 'react'

export function AutomotiveCredibilitySection() {
  const [hoveredCard, setHoveredCard] = useState<number | null>(null)
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
  const cardRefs = [useRef<HTMLDivElement>(null), useRef<HTMLDivElement>(null), useRef<HTMLDivElement>(null)]

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>, cardIndex: number) => {
    const card = cardRefs[cardIndex].current
    if (card) {
      const rect = card.getBoundingClientRect()
      const x = e.clientX - rect.left - rect.width / 2
      const y = e.clientY - rect.top - rect.height / 2
      setMousePosition({ x, y })
    }
  }

  return (
    <section className="relative py-24 overflow-hidden bg-gradient-to-b from-background via-muted/20 to-background">
      {/* Header */}
      <div className="container mx-auto px-6 mb-16">
        <div className="text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="reveal reveal-delay-0 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2F5FE8]/10 border border-[#2F5FE8]/20 mb-4"
          >
            <Sparkles className="h-4 w-4 text-[#2F5FE8]" />
            <span className="eyebrow">
              Real Results From Real Dealers
            </span>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="reveal reveal-delay-1 text-lg text-muted-foreground max-w-2xl mx-auto"
          >
            Built on the lead-and-follow-up systems behind a multi-rooftop dealer operation. Every lead answered fast, worked, and tracked.
          </motion.p>
        </div>
      </div>

      {/* Cards Grid */}
      <div className="container mx-auto px-6">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
          {/* Card 1: Stats */}
          <motion.div
            ref={cardRefs[0]}
            initial={{ opacity: 0, y: 30, filter: "blur(10px)" }}
            whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            viewport={{ once: true }}
            transition={{
              delay: 0.2,
              duration: 0.8,
              ease: [0.16, 1, 0.3, 1]
            }}
            onHoverStart={() => setHoveredCard(1)}
            onHoverEnd={() => setHoveredCard(null)}
            onMouseMove={(e) => handleMouseMove(e, 0)}
            className="reveal reveal-delay-0 lift-hover relative rounded-3xl overflow-hidden group bg-white dark:bg-zinc-900"
            style={{
              boxShadow: hoveredCard === 1
                ? '0 -10px 80px 10px rgba(47, 95, 232, 0.15), 0 20px 40px rgba(0, 0, 0, 0.12)'
                : '0 4px 20px rgba(0, 0, 0, 0.08)',
            }}
          >
            {/* Gradient Glow */}
            <motion.div
              className="absolute bottom-0 left-0 right-0 h-2/3"
              style={{
                background: 'radial-gradient(ellipse at bottom, rgba(47, 95, 232, 0.15) -20%, transparent 70%)',
                filter: 'blur(40px)',
              }}
              animate={{
                opacity: hoveredCard === 1 ? 1 : 0.6,
              }}
              transition={{
                duration: 0.4,
                ease: "easeOut"
              }}
            />

            {/* Bottom Border Glow */}
            <motion.div
              className="absolute bottom-0 left-0 right-0 h-[2px]"
              style={{
                background: 'linear-gradient(90deg, rgba(47, 95, 232, 0.1) 0%, rgba(47, 95, 232, 0.5) 50%, rgba(47, 95, 232, 0.1) 100%)',
              }}
              animate={{
                boxShadow: hoveredCard === 1
                  ? '0 0 20px 4px rgba(47, 95, 232, 0.4)'
                  : '0 0 15px 3px rgba(47, 95, 232, 0.2)',
                opacity: hoveredCard === 1 ? 1 : 0.9,
              }}
              transition={{
                duration: 0.4,
                ease: "easeOut"
              }}
            />

            {/* Content */}
            <div className="relative z-10 p-8">
              <motion.div
                className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 dark:from-[#2F5FE8]/20 dark:to-[#7EA0F5]/20 flex items-center justify-center mb-6 border border-blue-200 dark:border-[#2F5FE8]/20"
                initial={{ filter: "blur(3px)", opacity: 0.7 }}
                animate={{
                  filter: "blur(0px)",
                  opacity: 1,
                  y: hoveredCard === 1 ? -2 : 0,
                }}
                transition={{
                  duration: 0.4,
                  ease: "easeOut"
                }}
              >
                <TrendingUp className="h-6 w-6 text-[#2F5FE8] dark:text-[#7EA0F5]" />
              </motion.div>

              <div className="reveal reveal-delay-0 text-[10px] font-bold uppercase tracking-[0.2em] text-[#2F5FE8] dark:text-[#7EA0F5] mb-2">
                Founder's operator track record
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-3">
                6 rooftops • 700+ vehicles
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                Before Frayze, our founder built and ran this lead engine from inside a GTA dealer group: one stack powering BDC, sales, finance, and service.
              </p>

              <div className="space-y-3">
                <div className="reveal reveal-delay-1 flex items-start gap-2 text-sm text-foreground">
                  <Check className="h-4 w-4 text-[#2F5FE8] dark:text-[#7EA0F5] flex-shrink-0 mt-0.5" />
                  <span>400% lead lift • $1.7M saved</span>
                </div>
                <div className="reveal reveal-delay-2 flex items-start gap-2 text-sm text-foreground">
                  <Check className="h-4 w-4 text-[#2F5FE8] dark:text-[#7EA0F5] flex-shrink-0 mt-0.5" />
                  <span>5+ years automotive automation</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Card 2: Reviews */}
          <motion.div
            ref={cardRefs[1]}
            initial={{ opacity: 0, y: 30, filter: "blur(10px)" }}
            whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            viewport={{ once: true }}
            transition={{
              delay: 0.3,
              duration: 0.8,
              ease: [0.16, 1, 0.3, 1]
            }}
            onHoverStart={() => setHoveredCard(2)}
            onHoverEnd={() => setHoveredCard(null)}
            onMouseMove={(e) => handleMouseMove(e, 1)}
            className="reveal reveal-delay-1 lift-hover relative rounded-3xl overflow-hidden group bg-white dark:bg-zinc-900"
            style={{
              boxShadow: hoveredCard === 2
                ? '0 -10px 80px 10px rgba(47, 95, 232, 0.15), 0 20px 40px rgba(0, 0, 0, 0.12)'
                : '0 4px 20px rgba(0, 0, 0, 0.08)',
            }}
          >
            {/* Gradient Glow */}
            <motion.div
              className="absolute bottom-0 left-0 right-0 h-2/3"
              style={{
                background: 'radial-gradient(ellipse at bottom, rgba(47, 95, 232, 0.15) -20%, transparent 70%)',
                filter: 'blur(40px)',
              }}
              animate={{
                opacity: hoveredCard === 2 ? 1 : 0.6,
              }}
            />

            {/* Bottom Border Glow */}
            <motion.div
              className="absolute bottom-0 left-0 right-0 h-[2px]"
              style={{
                background: 'linear-gradient(90deg, rgba(47, 95, 232, 0.1) 0%, rgba(47, 95, 232, 0.5) 50%, rgba(47, 95, 232, 0.1) 100%)',
              }}
              animate={{
                boxShadow: hoveredCard === 2
                  ? '0 0 20px 4px rgba(47, 95, 232, 0.4)'
                  : '0 0 15px 3px rgba(47, 95, 232, 0.2)',
              }}
            />

            {/* Content */}
            <div className="relative z-10 p-8">
              <div className="text-center mb-6">
                <div className="text-2xl font-bold text-foreground mb-2">Real systems, running now</div>
                <p className="text-sm text-muted-foreground">Not a template. The lead engine from a multi-rooftop operation, built for your store.</p>
              </div>

              <div className="space-y-3">
                <div className="reveal reveal-delay-1 flex items-start gap-2 text-sm text-foreground bg-muted/50 rounded-lg p-3">
                  <Check className="h-4 w-4 text-[#2F5FE8] dark:text-[#7EA0F5] flex-shrink-0 mt-0.5" />
                  <span>Every lead answered in seconds</span>
                </div>
                <div className="reveal reveal-delay-2 flex items-start gap-2 text-sm text-foreground bg-muted/50 rounded-lg p-3">
                  <Check className="h-4 w-4 text-[#2F5FE8] dark:text-[#7EA0F5] flex-shrink-0 mt-0.5" />
                  <span>Speed-to-lead and show-rate tracked by rooftop</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Card 3: Testimonial */}
          <motion.div
            ref={cardRefs[2]}
            initial={{ opacity: 0, y: 30, filter: "blur(10px)" }}
            whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            viewport={{ once: true }}
            transition={{
              delay: 0.4,
              duration: 0.8,
              ease: [0.16, 1, 0.3, 1]
            }}
            onHoverStart={() => setHoveredCard(3)}
            onHoverEnd={() => setHoveredCard(null)}
            onMouseMove={(e) => handleMouseMove(e, 2)}
            className="reveal reveal-delay-2 lift-hover relative rounded-3xl overflow-hidden group bg-white dark:bg-zinc-900"
            style={{
              boxShadow: hoveredCard === 3
                ? '0 -10px 80px 10px rgba(47, 95, 232, 0.15), 0 20px 40px rgba(0, 0, 0, 0.12)'
                : '0 4px 20px rgba(0, 0, 0, 0.08)',
            }}
          >
            {/* Gradient Glow */}
            <motion.div
              className="absolute bottom-0 left-0 right-0 h-2/3"
              style={{
                background: 'radial-gradient(ellipse at bottom, rgba(47, 95, 232, 0.15) -20%, transparent 70%)',
                filter: 'blur(40px)',
              }}
              animate={{
                opacity: hoveredCard === 3 ? 1 : 0.6,
              }}
            />

            {/* Bottom Border Glow */}
            <motion.div
              className="absolute bottom-0 left-0 right-0 h-[2px]"
              style={{
                background: 'linear-gradient(90deg, rgba(47, 95, 232, 0.1) 0%, rgba(47, 95, 232, 0.5) 50%, rgba(47, 95, 232, 0.1) 100%)',
              }}
              animate={{
                boxShadow: hoveredCard === 3
                  ? '0 0 20px 4px rgba(47, 95, 232, 0.4)'
                  : '0 0 15px 3px rgba(47, 95, 232, 0.2)',
              }}
            />

            {/* Content */}
            <div className="relative z-10 p-8 flex flex-col h-full">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 dark:from-[#2F5FE8]/20 dark:to-[#7EA0F5]/20 flex items-center justify-center mb-6 border border-blue-200 dark:border-[#2F5FE8]/20">
                <Star className="h-6 w-6 text-[#2F5FE8] dark:text-[#7EA0F5] fill-[#2F5FE8] dark:fill-[#7EA0F5]" />
              </div>

              <blockquote className="reveal reveal-delay-0 text-lg text-foreground mb-4 flex-grow">
                "This is the system I ran across six rooftops. Now it's productized so any dealer can run it."
              </blockquote>

              <div className="reveal reveal-delay-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Denis Mahoney, Founder, Frayze
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
